import React from 'react';
import axios, { AxiosInstance, AxiosError } from 'axios';
import Cookies from 'js-cookie';
import {
  UnauthorizedView,
  ForbiddenView,
  NotFoundView,
  TooManyRequestsView,
} from '@/components/conversations/ErrorViews';
import { createRoot } from 'react-dom/client';
import { removeCookies } from './slices/authSlice';


export const APP_BASE_URL: string = process.env.NODE_ENV === 'production' ? process.env.NEXT_PUBLIC_APP_URL || 'https://ainativeforms.com' : 'http://localhost:3056';
export const API_BASE_URL: string = process.env.NODE_ENV === 'production' ? process.env.NEXT_API_BASE_URL || 'https://api.ainativeforms.com' : 'http://localhost';


export const BASE_URL = `${API_BASE_URL}/api/v1`; 


// Create base axios config that both instances will use
const createBaseConfig = () => ({
  baseURL: BASE_URL,
});

// Create the private instance (for authenticated routes)
const axiosInstance: AxiosInstance = axios.create(createBaseConfig());

// Create the public instance (for public routes)
const publicInstance: AxiosInstance = axios.create(createBaseConfig());

// Add FormData and auth token support for private instance
axiosInstance.interceptors.request.use(
  (config) => {
    const token = Cookies.get('accessToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
       config.headers['Content-Type'] = 'multipart/form-data';
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add FormData support for public instance (no auth token needed)
publicInstance.interceptors.request.use(
  (config) => {
    if (config.data instanceof FormData) {
      config.headers['Content-Type'] = 'multipart/form-data';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Define auth-related routes that should skip redirects
const AUTH_ROUTES = [
  '/user/login',
  '/user/logout',
  '/user/register',
  '/user/reset-password',
  '/user/forgot-password',
  '/user/verify-email',
  '/user/resend-verification',
  '/user/verify-code',
];

const PUBLIC_ROUTES = [
  '/templates/default/get-for-home-page'
]

// Helper function to check if a URL is an auth route
const isAuthRoute = (url: string | undefined): boolean => {
  if (!url) return false;
  return AUTH_ROUTES.some(route => url.includes(route));
};


const isPublicTemplate = (url: string | undefined): boolean => {
  if(!url) return false;
  return PUBLIC_ROUTES.some(route => url.includes(route))
}


// Private instance response interceptor (with auth redirects)
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      const status = error.response.status;
      const isAuthUrl = isAuthRoute(error.config?.url);
      const originalData = error.response.data as any;

      // Only handle redirects for non-auth routes
      if (!isAuthUrl) {
        switch (status) {
          case 401:
            removeCookies();
            if (typeof window !== 'undefined') {
              window.location.href = '/login';
            }
            break;
          case 403:
            removeCookies();
            if (typeof window !== 'undefined') {
                window.location.href = '/forbidden';
            }
            break;
          case 404:
            if (typeof window !== 'undefined') {
              window.location.href = '/not-found';
            }
            break;
          case 429:
            if (typeof window !== 'undefined') {
              // Check if this is a billing-related limit
              const isBillingRelated = originalData && 
                (originalData.billing_type || originalData.remaining_conversations !== undefined);
              
              // Check if this is a conversation-specific limit
              const isConversationRelated = originalData && originalData.type === 'conversation';
              
              if (isBillingRelated || isConversationRelated) {
                // Craft a more user-friendly message
                const userFriendlyMessage = originalData.message || 
                  "We've reached the monthly conversation limit for this form. This is a temporary limitation.";
                
                // Create URL with query parameters for billing info
                const params = new URLSearchParams({
                  billing_type: originalData.billing_type || 'subscription',
                  message: userFriendlyMessage,
                  remaining: originalData.remaining_conversations?.toString() || '0',
                  is_billing: isBillingRelated ? 'true' : 'false'
                });
                
                // Add type parameter if present
                if (isConversationRelated) {
                  params.append('type', 'conversation');
                }
                
                window.location.href = `/too-many-requests?${params.toString()}`;
              } else {
                // Regular rate limiting
                window.location.href = '/too-many-requests';
              }
            }
            break;
          default:
          console.error('Unhandled error status:', status);
        }
      } else {
        // For auth routes, just log the error without redirecting
        console.error(`Auth route error (${status}):`, error.response.data);
      }
    } else {
      console.error('Error with no response:', error.message);
    }

    return Promise.reject(error);
  }
);

// Public instance response interceptor
publicInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    console.log('error', error);
    const errorData = error;
    if (error.response) {
      console.log('error.response', error.response);
      const status = error.response.status;
      
      // Standardize error response format
      const originalData = error.response.data as any;
      const standardizedData = {
        success: false,
        message: originalData.message || 'An error occurred',
        errors: originalData.errors || null,
      };

      // Override the response data with standardized format
      error.response.data = standardizedData;

      // Create container for error view if it doesn't exist
      let errorContainer = document.getElementById('error-container');
      if (!errorContainer) {
        errorContainer = document.createElement('div');
        errorContainer.id = 'error-container';
        document.body.appendChild(errorContainer);
      }

      const root = createRoot(errorContainer);
    
      const isHomePage = isPublicTemplate(error.config?.url);
      if(!isHomePage){
        switch (status) {
          case 401:
            root.render(
              React.createElement(UnauthorizedView, {
                onRetry: () => {
                  root.unmount();
                  window.location.reload();
                },
              })
            );
            break;
          case 403:
             root.render(React.createElement(ForbiddenView));
            break;
          case 404:
             root.render(React.createElement(NotFoundView));
             break;
          case 429:
            // Check if this is a billing-related limit
            const isBillingRelated = originalData && 
              (originalData.billing_type || originalData.remaining_conversations !== undefined);
            
            // Check if this is a conversation-specific limit
            const isConversationRelated = originalData && originalData.type === 'conversation';
            
            if (isBillingRelated || isConversationRelated) {
              // Craft a more user-friendly message
              const userFriendlyMessage = originalData.message || 
                "We've reached the monthly conversation limit for this form. This is a temporary limitation.";
              
              // Extract billing information from the error response
              const billingInfo = {
                billingType: originalData.billing_type || 'subscription',
                message: userFriendlyMessage,
                remainingConversations: originalData.remaining_conversations || 0,
                // Add type if it's a conversation error
                ...(isConversationRelated && { type: 'conversation' })
              };
              
              root.render(
                React.createElement(TooManyRequestsView, billingInfo)
              );
            } else {
              // Regular rate limiting
              root.render(React.createElement(TooManyRequestsView));
            }
            break;
          default:
            console.error('Public API error:', status, error.message);
        }
      }else{
        return Promise.resolve(errorData);
      }
     
    } else {
      console.error('Public API error with no response:', error.message);
    }

    return Promise.reject(errorData);
  }
);

export { publicInstance };
export default axiosInstance;
